import { Card } from '@/components/ui/card';
import {
  marketRatesService,
  MarketColorTablesResponse,
} from '@/services/market-rates.service';
import { useEffect, useState } from 'react';

function formatMarketYieldRange(value: string): string {
  // Parse format like "5.25% - 6.50%" or "5.25 - 6.50" or "5.25% - 6.50"
  const match = value.match(/(\d+\.?\d*)\s*%?\s*-\s*(\d+\.?\d*)\s*%?/);
  if (match) {
    const lower = parseFloat(match[1]);
    const upper = parseFloat(match[2]);
    if (!isNaN(lower) && !isNaN(upper)) {
      return `${lower.toFixed(3)}% - ${upper.toFixed(3)}%`;
    }
  }
  // If parsing fails, return original value
  return value;
}

function getTerm(
  sector: Record<string, unknown>,
  bucket: string,
  field: 'Credit Spread Range' | 'Discount Margin Range' | 'Market Yield Range'
) {
  const obj = sector[bucket] as Record<string, string> | undefined;
  const value = obj && typeof obj[field] === 'string' ? obj[field] : null;
  
  if (!value || value === '—') return '—';
  
  // Format Market Yield Range with 3 decimals
  if (field === 'Market Yield Range') {
    return formatMarketYieldRange(value);
  }
  
  return value;
}

const MarketColorPage = () => {
  const [data, setData] = useState<MarketColorTablesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      try {
        setIsLoading(true);
        const response = await marketRatesService.getMarketColorTables();
        if (!isMounted) return;
        setData(response);
      } catch (e) {
        if (!isMounted) return;
        setError('Failed to load market color tables');
        console.error(e);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="page-container" data-test-id="market-color-page-container">
      <div className="page-inner" data-test-id="page-inner-container">
        <div className="mb-8" data-test-id="header-container">
          <h1
            className="text-2xl font-bold mb-2"
            data-test-id="market-color-subtitle"
          >
            Commercial Real Estate Debt Markets Overview and Current Pricing
          </h1>
        </div>

        {/* Senior, Secured Term Lending */}
        <Card className="p-6 mb-8" data-test-id="senior-secured-card">
          <h2 className="text-xl font-semibold" data-test-id="senior-title">
            {data ? data.senior_secured.title : 'Senior, Secured Term Lending'}
          </h2>
          <p className="text-sm text-gray-600 mb-4" data-test-id="senior-date">
            Reporting Date: {data ? data.senior_secured.reporting_date : '—'}
          </p>

          <div className="overflow-x-auto border border-gray-300 rounded-lg">
            <table
              className="relative table-responsive min-w-full divide-y divide-gray-200 overflow-hidden"
              data-test-id="senior-table"
            >
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300 "
                    rowSpan={2}
                  >
                    Sector
                  </th>
                  <th
                    className="px-1 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300"
                    colSpan={2}
                  >
                    Fixed Rate Loans
                  </th>
                  <th
                    className="px-1 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider"
                    colSpan={2}
                  >
                    Adjustable Rate Mortgages
                  </th>
                </tr>
                <tr>
                  <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    <p>Credit Spread Range</p>
                    <p className="text-[10px] normal-case text-gray-500">
                      (Lower - Upper)
                    </p>
                  </th>
                  <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    <p>Market Yield Range</p>
                    <p className="text-[10px] normal-case text-gray-500">
                      (Lower - Upper)
                    </p>
                  </th>
                  <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    <p>Discount Margin Range</p>
                    <p className="text-[10px] normal-case text-gray-500">
                      (Lower - Upper)
                    </p>
                  </th>
                  <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <p>Market Yield Range</p>
                    <p className="text-[10px] normal-case text-gray-500">
                      (Lower - Upper)
                    </p>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="table-cell text-center py-8">
                      Loading...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="table-cell text-center py-8 text-red-500"
                    >
                      {error}
                    </td>
                  </tr>
                ) : data ? (
                  data.senior_secured.sectors.map((s, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="pl-2 py-3 text-sm border-r border-gray-200">
                        {s.sector}
                      </td>
                      <td className="px-1 py-3 text-sm text-center border-r border-gray-200">
                        {getTerm(s, 'Fixed Rate Loans', 'Credit Spread Range')}
                      </td>
                      <td className="px-1 py-3 text-sm text-center border-r border-gray-200">
                        {getTerm(s, 'Fixed Rate Loans', 'Market Yield Range')}
                      </td>
                      <td className="px-1 py-3 text-sm text-center border-r border-gray-200">
                        {getTerm(
                          s,
                          'Adjustable Rate Mortgages',
                          'Discount Margin Range'
                        )}
                      </td>
                      <td className="px-1 py-3 text-sm text-center">
                        {getTerm(
                          s,
                          'Adjustable Rate Mortgages',
                          'Market Yield Range'
                        )}
                      </td>
                    </tr>
                  ))
                ) : null}
              </tbody>
            </table>
          </div>

          <p
            className="text-xs text-gray-500 mt-3"
            data-test-id="senior-source"
          >
            {data?.senior_secured.source ||
              'Cushman & Wakefield, Institutional Investor, Trepp, CMBX'}
          </p>
        </Card>

        {/* Construction Loan & Bridge Financing Market */}
        <Card className="p-6 mb-8" data-test-id="construction-bridge-card">
          <h2
            className="text-xl font-semibold"
            data-test-id="construction-title"
          >
            {data
              ? data.construction_bridge.title
              : 'Construction Loan & Bridge Financing Market'}
          </h2>
          <p
            className="text-sm text-gray-600 mb-4"
            data-test-id="construction-date"
          >
            Reporting Date:{' '}
            {data ? data.construction_bridge.reporting_date : '—'}
          </p>

          <div className="overflow-x-auto border border-gray-300 rounded-lg">
            <table
              className="relative table-responsive min-w-full divide-y divide-gray-200 overflow-hidden"
              data-test-id="construction-table"
            >
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300"
                    rowSpan={2}
                  >
                    Sector
                  </th>
                  <th
                    className="px-1 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300"
                    colSpan={2}
                  >
                    Ground-Up Construction
                  </th>
                  <th
                    className="px-1 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300"
                    colSpan={2}
                  >
                    Rehab Construction
                  </th>
                  <th
                    className="px-1 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider"
                    colSpan={2}
                  >
                    Transitional
                  </th>
                </tr>
                <tr>
                  <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    <p>Floating Rate Debt</p>
                    <p>Credit Spread Range</p>
                    <p className="text-[10px] normal-case text-gray-500">
                      (Lower - Upper)
                    </p>
                  </th>
                  <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    <p>Market Yield Range</p>
                    <p className="text-[10px] normal-case text-gray-500">
                      (Lower - Upper)
                    </p>
                  </th>
                  <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    <p>Floating Rate Debt</p>
                    <p>Credit Spread Range</p>
                    <p className="text-[10px] normal-case text-gray-500">
                      (Lower - Upper)
                    </p>
                  </th>
                  <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    <p>Market Yield Range</p>
                    <p className="text-[10px] normal-case text-gray-500">
                      (Lower - Upper)
                    </p>
                  </th>
                  <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    <p>Floating Rate Debt</p>
                    <p>Credit Spread Range</p>
                    <p className="text-[10px] normal-case text-gray-500">
                      (Lower - Upper)
                    </p>
                  </th>
                  <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <p>Market Yield Range</p>
                    <p className="text-[10px] normal-case text-gray-500">
                      (Lower - Upper)
                    </p>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="table-cell text-center py-8">
                      Loading...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="table-cell text-center py-8 text-red-500"
                    >
                      {error}
                    </td>
                  </tr>
                ) : data ? (
                  data.construction_bridge.sectors.map((s, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="pl-2 py-3 text-sm border-r border-gray-200">
                        {s.sector}
                      </td>
                      <td className="px-1 py-3 text-sm text-center border-r border-gray-200">
                        {getTerm(
                          s,
                          'Ground-Up Construction',
                          'Credit Spread Range'
                        )}
                      </td>
                      <td className="px-1 py-3 text-sm text-center border-r border-gray-200">
                        {getTerm(
                          s,
                          'Ground-Up Construction',
                          'Market Yield Range'
                        )}
                      </td>
                      <td className="px-1 py-3 text-sm text-center border-r border-gray-200">
                        {getTerm(
                          s,
                          'Rehab Construction',
                          'Credit Spread Range'
                        )}
                      </td>
                      <td className="px-1 py-3 text-sm text-center border-r border-gray-200">
                        {getTerm(s, 'Rehab Construction', 'Market Yield Range')}
                      </td>
                      <td className="px-1 py-3 text-sm text-center border-r border-gray-200">
                        {getTerm(s, 'Transitional', 'Credit Spread Range')}
                      </td>
                      <td className="px-1 py-3 text-sm text-center">
                        {getTerm(s, 'Transitional', 'Market Yield Range')}
                      </td>
                    </tr>
                  ))
                ) : null}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Select Data */}
        <Card className="p-6" data-test-id="select-data-card">
          <h2 className="text-xl font-semibold mb-2">Select Data</h2>
          <p className="text-sm text-gray-600 mb-4">
            Reporting Date: {data ? data.select_data.reporting_date : '—'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">3 year US Treasury</p>
              <p className="text-lg font-bold">
                {data ? data.select_data['3 year US Treasury'] : '—'}
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">5 year US Treasury</p>
              <p className="text-lg font-bold">
                {data ? data.select_data['5 year US Treasury'] : '—'}
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">SOFR (3month)</p>
              <p className="text-lg font-bold">
                {data ? data.select_data['SOFR (3month)'] : '—'}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MarketColorPage;
